/**
 * MapPage.tsx
 * Display information of a ticket
 */

// react
import { ReactNode, useEffect, useState } from 'react';

import { Flex, Text } from '@mantine/core';
import { useViewportSize } from '@mantine/hooks';
import { FullscreenControl, Map, Marker, NavigationControl, Popup, ScaleControl } from '@vis.gl/react-maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { storageService } from 'src/services/storageService';

const ICON = `M20.2,15.7L20.2,15.7c1.1-1.6,1.8-3.6,1.8-5.7c0-5.6-4.5-10-10-10S2,4.5,2,10c0,2,0.6,3.9,1.6,5.4c0,0.1,0.1,0.2,0.2,0.3
  c0,0,0.1,0.1,0.1,0.2c0.2,0.3,0.4,0.6,0.7,0.9c2.6,3.1,7.4,7.6,7.4,7.6s4.8-4.5,7.4-7.5c0.2-0.3,0.5-0.6,0.7-0.9
  C20.1,15.8,20.2,15.8,20.2,15.7z`;

// const PHOENIX = {
//     latitude: 33.448253,
//     longitude: -112.076617,
//     zoom: 8
// }

const SEATTLE = {
    latitude: 47.603776,
    longitude: -122.330765,
    zoom: 8
}

const DEFAULT_VIEW = SEATTLE

function Pin({ size = 20, location }) {
    switch (location.type) {
        case 'house':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-home">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M5 12l-2 0l9 -9l9 9l-2 0" />
                    <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7" />
                    <path d="M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6" />
                </svg>
            );
        case 'course':
        default:
            return (
                <svg height={size} viewBox="0 0 24 24" style={{
                    cursor: 'pointer',
                    fill: 'red',
                    stroke: 'none'
                }}>
                    <path d={ICON} />
                </svg>
            );
    }
}


const MapPage = () => {
    const [viewState, setViewState] = useState(DEFAULT_VIEW);
    const [pins, setPins] = useState<ReactNode[]>([]);
    const { height } = useViewportSize();
    const [locations, setLocations] = useState<any[]>([]);
    const [course, setCourse] = useState<any | null>(null);

    const mapStyle = process.env.REACT_APP_MAP_STYLE + '?key=' + process.env.REACT_APP_MAPTILER_API_KEY;

    useEffect(() => {
        storageService.getInfoFile('locations.json')
            .then((text) => setLocations(JSON.parse(text)))
            .catch((e) => console.error(e));
    }, [])

    useEffect(() => {
        setPins(locations
            .map((location, index) => (
                <Marker
                    key={`marker-${index}`}
                    longitude={location.longitude}
                    latitude={location.latitude}
                    anchor="bottom"
                    onClick={e => {
                        // If we let the click event propagates to the map, it will immediately close the popup
                        // with `closeOnClick: true`
                        e.originalEvent.stopPropagation();
                        setCourse(location);
                    }}
                >
                    <Pin location={location} />
                </Marker>
            )))
    }, [locations])

    return (
        <Flex h={height - 120} bg="gray.2">
            <Map
                {...viewState}
                onMove={evt => setViewState(evt.viewState)}
                mapStyle={mapStyle}
            >
                <FullscreenControl position="top-left" />
                <NavigationControl position="top-left" />
                <ScaleControl />
                {pins}
                {course && (
                    <Popup
                        anchor="top"
                        longitude={Number(course.longitude)}
                        latitude={Number(course.latitude)}
                        onClose={() => setCourse(null)}
                    >
                        {course.pic !== "" && <img src={course.pic} width={200} alt={course.name} />}
                        <Text>{course.name}</Text>
                    </Popup>
                )}
            </Map>
        </Flex>
    );
}

export default MapPage;
